import { useEffect } from "react";

const BASE_URL = "https://www.ordell.ro/webcare";

const organizationSchema = {
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  "name": "Ordell WebCare",
  "alternateName": "Ordell",
  "url": BASE_URL,
  "logo": `${BASE_URL}/images/ordell-logo.png`,
  "description": "Mentenanță și suport WordPress pentru business-uri care vor stabilitate, claritate și liniște operațională.",
  "foundingDate": "2017",
  "parentOrganization": {
    "@type": "Organization",
    "name": "SC Maze Digital SRL",
    "taxID": "RO38272886"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["Romanian"]
  },
  "sameAs": []
};

const websiteSchema = {
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  "url": BASE_URL,
  "name": "Ordell WebCare",
  "description": "Mentenanță și suport WordPress pentru site-uri de prezentare și magazine WooCommerce.",
  "publisher": { "@id": `${BASE_URL}/#organization` },
  "inLanguage": "ro-RO"
};

function buildBreadcrumbs(items) {
  return {
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": item.url
    }))
  };
}

function buildWebPage(page) {
  return {
    "@type": page.type || "WebPage",
    "@id": page.url,
    "url": page.url,
    "name": page.name,
    "description": page.description,
    "isPartOf": { "@id": `${BASE_URL}/#website` },
    "about": { "@id": `${BASE_URL}/#organization` },
    "inLanguage": "ro-RO"
  };
}

const schemas = {
  home: () => ({
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema,
      websiteSchema,
      buildWebPage({
        url: BASE_URL,
        name: "Ordell WebCare - Mentenanță și Suport WordPress",
        description: "Mentenanță și suport WordPress pentru site-uri de prezentare și magazine WooCommerce. Actualizări, backup-uri, monitorizare, securitate și intervenții rapide."
      }),
      buildBreadcrumbs([
        { name: "Acasă", url: BASE_URL }
      ]),
      {
        "@type": "Service",
        "serviceType": "WordPress Maintenance",
        "provider": { "@id": `${BASE_URL}/#organization` },
        "name": "Mentenanță WordPress",
        "description": "Actualizări, backup-uri, monitorizare, securitate și suport pentru site-uri WordPress și magazine WooCommerce.",
        "areaServed": { "@type": "Country", "name": "Romania" },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Pachete mentenanță WordPress",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": { "@type": "Service", "name": "Site de Prezentare" },
              "price": "250",
              "priceCurrency": "RON",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "250",
                "priceCurrency": "RON",
                "billingIncrement": 1,
                "unitCode": "MON",
                "referenceQuantity": { "@type": "QuantitativeValue", "value": "1", "unitCode": "MON" }
              },
              "description": "Mentenanță WordPress pentru site-uri de prezentare. Include actualizări, monitorizare, backup-uri, securitate și suport."
            },
            {
              "@type": "Offer",
              "itemOffered": { "@type": "Service", "name": "WooCommerce" },
              "price": "550",
              "priceCurrency": "RON",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "550",
                "priceCurrency": "RON",
                "billingIncrement": 1,
                "unitCode": "MON",
                "referenceQuantity": { "@type": "QuantitativeValue", "value": "1", "unitCode": "MON" }
              },
              "description": "Mentenanță WooCommerce cu backup-uri mai dese, verificări operaționale suplimentare și suport prioritar."
            }
          ]
        }
      }
    ]
  }),

  pachete: () => ({
    "@context": "https://schema.org",
    "@graph": [
      buildWebPage({
        url: `${BASE_URL}/pachete`,
        name: "Pachete Mentenanță WordPress - Ordell WebCare",
        description: "Pachete clare de mentenanță WordPress: Site de Prezentare (250 lei/lună), WooCommerce (de la 550 lei/lună) și Custom."
      }),
      buildBreadcrumbs([
        { name: "Acasă", url: BASE_URL },
        { name: "Pachete", url: `${BASE_URL}/pachete` }
      ]),
      {
        "@type": "Product",
        "name": "Site de Prezentare - Mentenanță WordPress",
        "description": "Pentru site-uri WordPress de prezentare, business-uri locale și companii care au nevoie de un site actualizat, sigur și gestionat corect.",
        "brand": { "@id": `${BASE_URL}/#organization` },
        "offers": {
          "@type": "Offer",
          "price": "250",
          "priceCurrency": "RON",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": "2026-12-31",
          "url": `${BASE_URL}/pachete`
        }
      },
      {
        "@type": "Product",
        "name": "WooCommerce - Mentenanță WordPress",
        "description": "Pentru magazine online unde orice problemă poate afecta direct comenzile, checkout-ul și încrederea clienților.",
        "brand": { "@id": `${BASE_URL}/#organization` },
        "offers": {
          "@type": "Offer",
          "price": "550",
          "priceCurrency": "RON",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": "2026-12-31",
          "url": `${BASE_URL}/pachete`
        }
      }
    ]
  }),

  servicii: () => ({
    "@context": "https://schema.org",
    "@graph": [
      buildWebPage({
        url: `${BASE_URL}/servicii`,
        name: "Servicii WordPress - Ordell WebCare",
        description: "Mentenanță, suport, WooCommerce care, backup, securitate, monitorizare uptime, intervenții și optimizare performanță.",
        type: "WebPage"
      }),
      buildBreadcrumbs([
        { name: "Acasă", url: BASE_URL },
        { name: "Servicii", url: `${BASE_URL}/servicii` }
      ]),
      ...[
        { name: "Mentenanță WordPress", desc: "Actualizări gestionate proactiv, verificări și mentenanță recurentă." },
        { name: "Suport WordPress", desc: "Intervenții clare și rapide pentru erori, incidente și ajustări minore." },
        { name: "WooCommerce Care", desc: "Suport prioritar și verificări suplimentare pentru magazine online." },
        { name: "Backup și restaurare", desc: "Backup-uri recurente și restaurare în caz de incident." },
        { name: "Securitate și hardening", desc: "Verificări malware și măsuri de protecție pentru un site mai sigur." },
        { name: "Monitorizare și uptime", desc: "Vizibilitate mai bună asupra funcționării site-ului și reacție la probleme." },
        { name: "Intervenții și remedieri", desc: "Intervenții organizate și documentate pentru probleme tehnice." },
        { name: "Infrastructură și stabilitate", desc: "Mediu mai potrivit pentru trafic, continuitate și predictibilitate." },
        { name: "Optimizări de performanță", desc: "Încărcare mai rapidă, caching corect și recomandări tehnice utile." },
      ].map(s => ({
        "@type": "Service",
        "name": s.name,
        "description": s.desc,
        "provider": { "@id": `${BASE_URL}/#organization` },
        "areaServed": { "@type": "Country", "name": "Romania" }
      }))
    ]
  }),

  despre: () => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        ...buildWebPage({
          url: `${BASE_URL}/despre`,
          name: "Despre Ordell WebCare - Mentenanță WordPress Profesională",
          description: "Ordell WebCare oferă mentenanță și suport WordPress pentru companii care vor un site stabil, sigur și gestionat corect.",
          type: "AboutPage"
        })
      },
      buildBreadcrumbs([
        { name: "Acasă", url: BASE_URL },
        { name: "Despre", url: `${BASE_URL}/despre` }
      ]),
      organizationSchema
    ]
  }),

  faq: (faqItems) => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "@id": `${BASE_URL}/faq`,
        "url": `${BASE_URL}/faq`,
        "name": "Întrebări Frecvente - Ordell WebCare",
        "description": "Răspunsuri la cele mai frecvente întrebări despre mentenanță WordPress, WooCommerce, backup-uri, securitate și suport.",
        "isPartOf": { "@id": `${BASE_URL}/#website` },
        "inLanguage": "ro-RO",
        "mainEntity": faqItems.map(item => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a
          }
        }))
      },
      buildBreadcrumbs([
        { name: "Acasă", url: BASE_URL },
        { name: "FAQ", url: `${BASE_URL}/faq` }
      ])
    ]
  }),

  contact: () => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        ...buildWebPage({
          url: `${BASE_URL}/contact`,
          name: "Cere Audit Gratuit - Ordell WebCare",
          description: "Cere un audit gratuit pentru site-ul tău WordPress. Îți spunem ce riscuri vedem, ce pachet ți se potrivește și ce pași recomandăm.",
          type: "ContactPage"
        })
      },
      buildBreadcrumbs([
        { name: "Acasă", url: BASE_URL },
        { name: "Contact", url: `${BASE_URL}/contact` }
      ])
    ]
  }),

  b2b: () => ({
    "@context": "https://schema.org",
    "@graph": [
      buildWebPage({
        url: `${BASE_URL}/b2b`,
        name: "Externalizare Mentenanță WordPress pentru Agenții - Ordell WebCare",
        description: "Externalizează mentenanța WordPress, suportul WooCommerce și hostingul. Parteneriat white-label sau co-managed."
      }),
      buildBreadcrumbs([
        { name: "Acasă", url: BASE_URL },
        { name: "B2B: Externalizare WP", url: `${BASE_URL}/b2b` }
      ]),
      {
        "@type": "Service",
        "name": "Externalizare Mentenanță WordPress B2B",
        "description": "Parteneriat tehnic pentru agenții, studiouri și freelanceri care vor să externalizeze mentenanța WordPress și suportul WooCommerce.",
        "provider": { "@id": `${BASE_URL}/#organization` },
        "areaServed": { "@type": "Country", "name": "Romania" },
        "audience": {
          "@type": "BusinessAudience",
          "audienceType": "Agenții de marketing, studiouri de design, freelanceri, companii IT"
        }
      }
    ]
  }),
};

export function JsonLd({ page, data }) {
  useEffect(() => {
    const schemaFn = schemas[page];
    if (!schemaFn) return;

    const schema = data ? schemaFn(data) : schemaFn();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    script.id = `jsonld-${page}`;

    const existing = document.getElementById(`jsonld-${page}`);
    if (existing) existing.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(`jsonld-${page}`);
      if (el) el.remove();
    };
  }, [page, data]);

  return null;
}
